<template>
  <div class="reports-view pa-3">
    <!-- Header bar -->
    <div class="reports-header">
      <div class="reports-filters">
        <Select
          label="Project"
          :items="reportProjectItems"
          :value="filters.projectId"
          @input="filters.projectId = $event"
          style="min-width:160px;"
        />
        <DateTimePicker
          :range="true"
          :value="[filters.start, filters.end]"
          label="Period"
          @input="updateTimeFilter"
          style="min-width:240px;"
        />
      </div>
      <div class="reports-stats">
        <div class="report-stat">
          <span class="report-stat__label">Total time</span>
          <span class="report-stat__value">{{ totalHoursFormatted }}</span>
          <span class="report-stat__sub">{{ totalHoursDecimal }}</span>
        </div>
        <div class="report-stat report-stat--earnings" v-if="totalEarnings !== null">
          <span class="report-stat__label">Earnings</span>
          <span class="report-stat__value">{{ totalEarningsFormatted }}</span>
        </div>
      </div>
      <Button
        @click="dialogs.pdf.dialog = true"
        class="small"
        variant="smooth primary"
      >
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="width:13px;height:13px;margin-right:5px;">
          <path d="M4 12H3a1 1 0 01-1-1V3a1 1 0 011-1h7l3 3v6a1 1 0 01-1 1h-1M8 15v-6M5 12l3 3 3-3"/>
        </svg>
        Open rapport
      </Button>
    </div>

    <!-- Table -->
    <div class="reports-table">
      <!-- Column headers -->
      <div class="reports-table__head">
        <SortableCol class="reports-col--date"    label="Start – End"  sort-key="start"       :sort-data="sortData" @click="sort('start')" />
        <SortableCol class="reports-col--hours"   label="Hours"        sort-key="amountHours" :sort-data="sortData" @click="sort('amountHours')" />
        <SortableCol class="reports-col--desc"    label="Description"  sort-key="description" :sort-data="sortData" @click="sort('description')" />
        <SortableCol class="reports-col--project" label="Project"      sort-key="projectId"   :sort-data="sortData" @click="sort('projectId')" />
        <div class="reports-col reports-col--earning">Earning/h</div>
        <div class="reports-col reports-col--actions">Actions</div>
      </div>

      <!-- Rows -->
      <div
        class="reports-table__body"
        ref="pageContainer"
        style="overflow:auto; flex:1;"
      >
        <Pagination
          @updateNumberOfPages="numberOfPages = $event"
          :items="timeEntries"
          key="itemId"
          :pageSize="pageSize"
          :page="page"
        >
          <template v-slot="{ item }">
            <ReportEntry :value="item" />
          </template>
        </Pagination>
      </div>

      <!-- Pagination -->
      <div class="reports-pagination" v-if="numberOfPages > 1">
        <button class="rpag-btn" :disabled="page <= 1" @click="switchPage(-1)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12L6 8l4-4"/></svg>
        </button>
        <template v-for="n in paginationPages" :key="n">
          <span v-if="n === '...'" class="rpag-ellipsis">…</span>
          <button v-else class="rpag-btn rpag-page" :class="{ active: page === n }" @click="page = n">{{ n }}</button>
        </template>
        <button class="rpag-btn" :disabled="page >= numberOfPages" @click="switchPage(1)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4l4 4-4 4"/></svg>
        </button>
        <span class="rpag-info">{{ page }} / {{ numberOfPages }}</span>
      </div>
    </div>

    <!-- PDF Dialog -->
    <Dialog
      :value="dialogs.pdf.dialog"
      width="90%"
      @close="dialogs.pdf.dialog = false"
    >
      <ReportDisplay
        :timeEntries="timeEntries"
        :fileName="pdfFileName"
      />
    </Dialog>
  </div>
</template>

<script>
  import moment from 'moment'
  import Select from '../common/Select.vue'
  import DateTimePicker from '../common/DateTimePicker.vue'
  import ReportEntry from '../modules/ReportEntry.vue'
  import Pagination from '../common/Pagination.vue'
  import Button from '../common/Button.vue'
  import Dialog from '../common/Dialog.vue'
  import ReportDisplay from '../modules/ReportDisplay.vue'
  import SortableCol from '../common/SortableCol.vue'
  import {
    getFirstLineFromHtml,
    durationAsSeconds,
    secondsFormat
  } from '@/utils'

  export default {
    name: 'Reports',
    components: {
      DateTimePicker,
      Select,
      ReportEntry,
      Pagination,
      Button,
      Dialog,
      ReportDisplay,
      SortableCol
    },
    data: () => ({
      dialogs: {
        pdf: {
          dialog: false
        }
      },
      filters: {
        start: moment().startOf('month').format(),
        end: moment().endOf('month').format(),
        projectId: null
      },
      sortData: {
        key: 'start',
        ascending: false
      },
      loading: {},
      numberOfPages: 0,
      pageSize: 10,
      page: 1
    }),
    watch: {
      '$store.getters.screenHeight'() {
        this.updatePageSize()
      }
    },
    mounted() {
      this.updatePageSize()
    },
    computed: {
      timeEntries() {
        const filterStart = moment(this.filters.start)
        const filterEnd = this.filters.end ? moment(this.filters.end) : null
        const nowStr = moment().format()
        const { key: sortKey, ascending } = this.sortData

        // Build project name lookup once — O(n) instead of O(n) per comparison
        const projectNameById = {}
        for (const p of this.projects) projectNameById[p.id] = p.title

        // Filter
        const filtered = this.$store.getters.timeEntries.filter((entry) => {
          const s = moment(entry.start)
          if (s.isBefore(filterStart)) return false
          if (filterEnd && s.isAfter(filterEnd)) return false
          if (this.filters.projectId && entry.projectId !== this.filters.projectId) return false
          return true
        })

        // Pre-compute sort value per entry once — O(n) instead of O(n log n)
        const decorated = filtered.map((entry) => {
          let v
          if (sortKey === 'amountHours') {
            v = durationAsSeconds(entry.start, entry.end || nowStr)
          } else if (sortKey === 'projectId') {
            v = projectNameById[entry.projectId] || ''
          } else if (sortKey === 'description') {
            v = getFirstLineFromHtml(entry.description)
          } else {
            v = entry[sortKey]
          }
          return { entry, v }
        })

        decorated.sort((a, b) => {
          if (a.v < b.v) return ascending ? -1 : 1
          if (a.v > b.v) return ascending ? 1 : -1
          return 0
        })

        return decorated.map(({ entry }) => entry)
      },
      projects() {
        return this.$store.getters.projects
      },
      projectItems() {
        return this.$store.getters.projectItems
      },
      reportProjectItems() {
        return [{ value: null, text: 'All projects' }, ...this.projectItems]
      },
      totalSeconds() {
        let total = 0
        for (const timeEntry of this.timeEntries) {
          total += durationAsSeconds(
            timeEntry.start,
            timeEntry.end || moment().format()
          )
        }
        return total
      },
      totalHoursFormatted() {
        return secondsFormat(this.totalSeconds)
      },
      totalHoursDecimal() {
        const h = Math.round((this.totalSeconds / 3600) * 100) / 100
        return h + 'h'
      },
      totalEarnings() {
        let hasEarnings = false
        let total = 0
        for (const timeEntry of this.timeEntries) {
          if (timeEntry.earningPerHour) {
            hasEarnings = true
            const hours = durationAsSeconds(
              timeEntry.start,
              timeEntry.end || moment().format()
            ) / 3600
            total += hours * timeEntry.earningPerHour
          }
        }
        if (!hasEarnings) return null
        return total
      },
      totalEarningsFormatted() {
        if (this.totalEarnings === null) return null
        return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(this.totalEarnings)
      },
      paginationPages() {
        const total = this.numberOfPages
        const current = this.page
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
        const pages = []
        pages.push(1)
        if (current > 3) pages.push('...')
        for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
          pages.push(i)
        }
        if (current < total - 2) pages.push('...')
        pages.push(total)
        return pages
      },
      pdfFileName() {
        let timeRangeText = [
          this.filters.projectId
            ? this.getProjectName(this.filters.projectId)
            : ''
        ]

        let isFullMonth = this.filters.end &&
          moment(this.filters.start)
            .startOf('month')
            .isSame(this.filters.start, 'day') &&
          moment(this.filters.start)
            .endOf('month')
            .isSame(this.filters.end, 'day')

        let isFullYear = this.filters.end &&
          moment(this.filters.start)
            .startOf('year')
            .isSame(this.filters.start, 'day') &&
          moment(this.filters.start)
            .endOf('year')
            .isSame(this.filters.end, 'day')

        if (isFullMonth) {
          timeRangeText.push(moment(this.filters.start).format('MMMM YYYY'))
        } else if (isFullYear) {
          timeRangeText.push(moment(this.filters.start).format('YYYY'))
        } else if (!this.filters.end) {
          timeRangeText.push('From ' + moment(this.filters.start).format('D MMM YYYY'))
        }

        return timeRangeText.filter((part) => !!part).join(' ')
      }
    },
    methods: {
      getProjectName(projectId) {
        return this.projects.find((p) => p.id === projectId).title
      },
      updatePageSize() {
        this.$nextTick(() => {
          const container = this.$refs.pageContainer
          if (container && container.clientHeight > 0) {
            this.pageSize = Math.max(1, Math.floor(container.clientHeight / 53))
          }
          if (this.page > this.numberOfPages) this.page = Math.max(1, this.numberOfPages)
        })
      },
      updateTimeFilter(data) {
        this.filters.start = data[0]
        this.filters.end = data[1]
      },
      sort(key) {
        if (this.sortData.key === key) {
          this.sortData.ascending = !this.sortData.ascending
        } else {
          this.sortData.key = key
          this.sortData.ascending = false
        }
      },
      switchPage(pageChange) {
        this.page += pageChange
        if (this.page < 1) this.page = 1
        else if (this.page > this.numberOfPages) this.page = this.numberOfPages
      }
    }
  }
</script>

<style scoped>
.reports-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Header bar */
.reports-header {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  padding: 0 0 16px;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--app-border);
  margin-bottom: 0;
}

.reports-filters {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex: 1;
  flex-wrap: wrap;
}

.reports-stats {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  flex-shrink: 0;
}

.report-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid var(--app-border);
  border-radius: 8px;
  padding: 8px 14px;
  min-width: 130px;
}

.report-stat--earnings {
  background: var(--primary-muted);
  border-color: rgba(61, 106, 144, 0.2);
}

.report-stat__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--app-text-muted);
}

.report-stat__value {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--app-text);
  line-height: 1.2;
}

.report-stat--earnings .report-stat__value {
  color: var(--primary);
}

.report-stat__sub {
  font-size: 0.7rem;
  color: var(--app-text-muted);
}

/* Table */
.reports-table {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.reports-table__head {
  display: flex;
  align-items: center;
  padding: 10px 0 10px 0;
  border-bottom: 1px solid var(--app-border);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--app-text-muted);
}

.reports-col {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  transition: color 0.12s;
  padding: 0 8px;
}

.reports-col:hover { color: rgba(0,0,0,0.7); }

.reports-col--date { flex: 4; min-width: 0; }
.reports-col--hours { flex: 1; min-width: 0; }
.reports-col--desc { flex: 2; min-width: 0; }
.reports-col--project { flex: 2; min-width: 0; }
.reports-col--earning { flex: 1; min-width: 0; cursor: default; }
.reports-col--actions { flex: 0 0 88px; cursor: default; }

.reports-table__body {
  flex: 1;
  overflow: auto;
  min-height: 0;
  padding-right: 4px;
}

/* Pagination */
.reports-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 0 4px;
  border-top: 1px solid var(--app-border);
}

.rpag-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 6px;
  border: 1px solid var(--app-border);
  border-radius: 6px;
  background: none;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--app-text-muted);
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}

.rpag-btn svg { width: 14px; height: 14px; }

.rpag-btn:hover:not(:disabled) {
  background: rgba(0,0,0,0.04);
  color: var(--app-text);
}

.rpag-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.rpag-btn.rpag-page.active {
  background: var(--primary);
  border-color: var(--primary);
  color: white;
}

.rpag-ellipsis {
  font-size: 0.8rem;
  color: var(--app-text-muted);
  padding: 0 2px;
  line-height: 30px;
}

.rpag-info {
  font-size: 0.72rem;
  color: var(--app-text-muted);
  margin-left: 8px;
  white-space: nowrap;
}
</style>
